import { BarChartOutlined } from '@ant-design/icons';
import { Button, Popover, Typography } from 'antd';
import cn from 'classnames';

import type { GetFinancialStatisticsResponse } from '@app/api/Analytics';

import { useCalendarHeaderStatsModel } from '../hooks';

import type { FC } from 'react';

import '../styles/CalendarHeaderStats.scss';

const { Text } = Typography;

interface CalendarHeaderStatsProps {
  financialStatistics?: GetFinancialStatisticsResponse;
  isLoading: boolean;
  dateString: string;
  showText: boolean;
}

interface MetricItem {
  key: string;
  label: string;
  value: string;
}

type SummaryCounterTone = 'success' | 'danger';

interface SummaryCounter {
  key: string;
  label: string;
  value: string;
  tone: SummaryCounterTone;
}

interface SummaryItem {
  dateLabel: string;
  counters: SummaryCounter[];
}

interface CalendarHeaderStatsContentProps {
  isLoading: boolean;
  summary: SummaryItem;
  detailRows: MetricItem[];
}

const CalendarHeaderStatsContent: FC<CalendarHeaderStatsContentProps> = ({
  isLoading,
  summary: { dateLabel, counters },
  detailRows,
}) => {
  return (
    <div
      className={cn('CalendarHeaderStats', {
        'CalendarHeaderStats--loading': isLoading,
      })}
      data-testid="calendar-header-stats-popover"
    >
      <div className="CalendarHeaderStats__summary">
        <Text className="CalendarHeaderStats__summary-date">{dateLabel}</Text>
        <div className="CalendarHeaderStats__summary-counters">
          {counters.map(({ key, label, value, tone }) => (
            <Text
              key={key}
              className={cn('CalendarHeaderStats__summary-counter', {
                [`CalendarHeaderStats__summary-counter--${tone}`]: tone,
              })}
            >
              {label}: {value}
            </Text>
          ))}
        </div>
      </div>

      <div className="CalendarHeaderStats__list">
        {detailRows.map(({ key, label, value }) => (
          <div key={key} className="CalendarHeaderStats__row">
            <Text className="CalendarHeaderStats__label">{label}</Text>
            <Text className="CalendarHeaderStats__value">{value}</Text>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CalendarHeaderStats: FC<CalendarHeaderStatsProps> = ({
  financialStatistics,
  isLoading,
  dateString,
  showText,
}) => {
  const { isOpen, triggerText, summary, detailRows, handleOpenChange } =
    useCalendarHeaderStatsModel({ financialStatistics, dateString });

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={isOpen}
      onOpenChange={handleOpenChange}
      classNames={{ root: 'CalendarHeaderStats__overlay' }}
      content={
        <CalendarHeaderStatsContent
          isLoading={isLoading}
          summary={summary}
          detailRows={detailRows}
        />
      }
    >
      <Button
        icon={<BarChartOutlined />}
        className="CalendarHeaderStats__trigger"
        data-testid="calendar-header-stats"
        aria-label="Открыть статистику за день"
        loading={isLoading}
      >
        <span className="CalendarHeaderStats__trigger-text">
          {showText && (
            <span className="CalendarHeaderStats__trigger-label">
              Статистика
            </span>
          )}
          <span className="CalendarHeaderStats__trigger-meta">
            {triggerText}
          </span>
        </span>
      </Button>
    </Popover>
  );
};
